import { Autocomplete, Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useContext } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { Field, FieldType } from '../../../types/FieldCommon';


interface ICustomInputFieldsProps {
  fields: Field[];
  updateFieldValue: (label: string, value: any) => void;
}

const CustomInputFields = ({ fields, updateFieldValue }: ICustomInputFieldsProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFieldValue(event.target.id, event.target.value);
  };

  const handleCheckboxChange = (label: string, checked: boolean) => {
    updateFieldValue(label, checked);
  };

  const { disabled } = useContext(DisableContext);

  const fieldEdit = (field: Field) => {
    switch (field.type) {
      case FieldType.DATETIME:
        return (
          <DateTimePicker
            label={field.label}
            value={typeof field.value === 'string' ? DateTime.fromISO(field.value) : null}
            onChange={(newValue) => updateFieldValue(field.label, newValue?.toISO())}
            disabled={disabled}
          />
        );

      case FieldType.DATE:
        return (
          <DatePicker
            label={field.label}
            value={typeof field.value === 'string' ? DateTime.fromISO(field.value) : null}
            onChange={(newValue) => updateFieldValue(field.label, newValue?.toISODate())}
            disabled={disabled}
          />
        );

      case FieldType.TIME:
        return (
          <TimePicker
            label={field.label}
            value={typeof field.value === 'string' ? DateTime.fromISO(field.value) : null}
            onChange={(newValue) => updateFieldValue(field.label, newValue?.toISO())}
            disabled={disabled}
            slotProps={{
              textField: {
                inputProps: { sx: { p: '8px' } },
              },
            }}
          />
        );

      case FieldType.AUTOCOMPLETE:
        return (
          <Autocomplete
            options={(field.options ?? []).map(String)}
            onChange={(_, value) => updateFieldValue(field.label, value)}
            value={field.value !== null ? String(field.value) : ' '}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label={field.label} />}
          />
        );

      case FieldType.MULTISELECT:
        return (
          <Autocomplete<string, true>
            multiple
            options={(field.options ?? []).map(String)}
            onChange={(_, value) => updateFieldValue(field.label, value)}
            value={Array.isArray(field.value) ? field.value.map(String) : []}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label={field.label} />}
          />
        );

      case FieldType.CHECKBOX:
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!field.value}
                onChange={(e) => handleCheckboxChange(field.label, e.target.checked)}
                name={field.label}
                color="primary"
                disabled={disabled}
              />
            }
            label={field.label}
          />
        );

      case FieldType.MULTILINE:
        return (
          <TextField
            multiline
            maxRows={10}
            type={field.type}
            id={field.label}
            label={field.label}
            onChange={handleChange}
            value={field.value ?? ''}
            disabled={disabled}
          />
        );

      default:
        return (
          <TextField
            type={field.type}
            id={field.label}
            label={field.label}
            onChange={handleChange}
            value={field.value ?? ''}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {_.map(fields, (field, index) => (
        <Box my={1} key={index}>
          {fieldEdit(field)}
        </Box>
      ))}
    </Box>
  );
};

export default CustomInputFields;
